import React, { useMemo, useState } from 'react';

import PopOver from 'components/common/popover';
import Loading from 'components/common/loading/Loading';

import { ROLE_PERMISSION } from 'constants/permissions';
import { PERMISSION_GRANT_SUCCESS_MESSAGE, PERMISSION_REVOKE_SUCCESS_MESSAGE } from 'constants/appConstants';

import { useUserPermissions } from 'hooks/query/useUserInfo';
import { success } from 'utils/toast';

import type { PermissionItem, RoleItem, RolePermissionRecord } from '../RolesPermissions';
import { useToggleRolePermissionMutation } from 'hooks/query/useRolesPermissions';

type UpdatingPermissionsState = Record<string, boolean>;

type RolesPermissionsMap = Map<number, number[]>;

type Props = {
  roles: RoleItem[];
  permissions: PermissionItem[];
  rolesPermissions: RolePermissionRecord[];
};

const RolesPermissionsTable: React.FC<Props> = ({
  roles,
  permissions,
  rolesPermissions: rolesPermissionsFromProps,
}) => {
  const [updatingRolesPermissions, setUpdatingRolesPermissions] = useState<UpdatingPermissionsState>({});
  const currentUserPermissions = useUserPermissions();
  const toggleRolePermissionMutation = useToggleRolePermissionMutation();

  const rolesPermissions = useMemo<RolesPermissionsMap>(() => {
    const nextRolesPermissions: RolesPermissionsMap = new Map();

    rolesPermissionsFromProps.forEach((role) => {
      nextRolesPermissions.set(
        role.id,
        role.permissions.reduce<number[]>((permissionIds, currentPermission) => {
          permissionIds.push(currentPermission.id);

          return permissionIds;
        }, [])
      );
    });

    return nextRolesPermissions;
  }, [rolesPermissionsFromProps]);

  const checkRolePermission = (roleId: number, permissionId: number) => {
    const assignedPermissions = rolesPermissions.get(roleId);

    if (!assignedPermissions?.length) {
      return false;
    }

    return assignedPermissions.includes(permissionId);
  };

  const rolePermissionIsUpdating = (roleId: number, permissionId: number) => {
    return Object.prototype.hasOwnProperty.call(updatingRolesPermissions, `${roleId}-${permissionId}`);
  };

  const getPermissionsName = (permissionLabel: string) => {
    const separatedWords = permissionLabel.split('.');

    return [separatedWords[separatedWords.length - 1], ...separatedWords.slice(0, -1)].join(' ');
  };

  const handlePermissionChange = (roleId: number, permission: PermissionItem) => {
    const permissionId = permission.id;

    setUpdatingRolesPermissions((prevState) => ({
      ...prevState,
      [`${roleId}-${permissionId}`]: true,
    }));

    const shouldRevoke = checkRolePermission(roleId, permissionId);

    toggleRolePermissionMutation.mutate(
      { roleId, permission, shouldRevoke },
      {
        onSuccess: () => {
          success({
            title: 'Success',
            message: shouldRevoke ? PERMISSION_REVOKE_SUCCESS_MESSAGE : PERMISSION_GRANT_SUCCESS_MESSAGE,
          });
        },
        onSettled: () => {
          setUpdatingRolesPermissions((prevState) => {
            const nextState = { ...prevState };
            delete nextState[`${roleId}-${permissionId}`];

            return nextState;
          });
        },
      }
    );
  };

  return (
    <div className="roles-permissions custom-scroll-bar">
      <table className="roles-permissions-table">
        <thead className="roles-permissions-table__header">
          <tr className="roles-permissions-table__row">
            <th className="roles-permissions-table__head">Actions</th>
            {roles.map((role) => (
              <th className="roles-permissions-table__head" key={role.id}>
                {role.name}
                <br />
                <span className="roles-permissions-table__id">({role.id})</span>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="roles-permissions-table__row">
          {permissions.map((permission) => (
            <tr className="roles-permissions-table__row" key={permission.id}>
              <td className="roles-permissions-table__data roles-permissions-table__data--head capital-text">
                {getPermissionsName(permission.label)}{' '}
                <span className="roles-permissions-table__id">({permission.id})</span>
              </td>
              {roles.map((role) => (
                <td className="roles-permissions-table__data" key={`${role.id}-${permission.id}`}>
                  {rolePermissionIsUpdating(role.id, permission.id) ? (
                    <Loading />
                  ) : (
                    <PopOver interactive html={<>{role.name}</>}>
                      <div className="roles-permissions-table__checkbox">
                        <input
                          disabled={
                            !currentUserPermissions[ROLE_PERMISSION.CREATE] &&
                            !currentUserPermissions[ROLE_PERMISSION.DELETE]
                          }
                          id={`${role.id}-${permission.id}`}
                          type="checkbox"
                          name="roles-permissions-table-checkmark"
                          className="roles-permissions-table__checkbox-btn"
                          checked={checkRolePermission(role.id, permission.id)}
                          onChange={() => {
                            handlePermissionChange(role.id, permission);
                          }}
                        />
                        <label htmlFor={`${role.id}-${permission.id}`} className="roles-permissions-table__label" />
                      </div>
                    </PopOver>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default React.memo(RolesPermissionsTable);
